import type { BuilderState } from "../builder-state";
import {
  qIdentMysql,
  qLiteral,
  NO_CAPS,
  type ConnectionString,
  type DialectCapabilities,
} from "./shared";

export const label = "MySQL / MariaDB";
export const defaultPort = 3306;

export const caps: DialectCapabilities = {
  ...NO_CAPS,
  hostPattern: true,
  truncatePriv: true,
  referencesAndTrigger: true,
  quoteIdentifiers: true,
  // MySQL has no SUPERUSER role attribute — keep superuserToggle off.
  connectionLimit: true,
  validUntil: true,
};

export function generate(s: BuilderState): string {
  const out: string[] = [];
  const push = (l = "") => out.push(l);
  const c = (t: string) => s.includeComments && push(`-- ${t}`);

  const db = qIdentMysql(s.dbName);
  const userQ = `${qLiteral(s.userName)}@${qLiteral(s.hostPattern || "%")}`;
  const schemaTarget = `${db}.*`;

  c("============================================================");
  c(`MySQL / MariaDB — ${s.mode === "create" ? "Create" : "Update"} user & database`);
  c("Run as root or a user with GRANT OPTION. Review before applying.");
  c("============================================================");
  push();

  if (s.mode === "create") {
    push(`CREATE DATABASE IF NOT EXISTS ${db} CHARACTER SET utf8mb4 COLLATE utf8mb4_unicode_ci;`);
    push();
  }

  if (s.mode === "create") {
    push(
      `CREATE USER IF NOT EXISTS ${userQ} IDENTIFIED BY ${qLiteral(s.password || "CHANGE_ME")};`,
    );
  } else if (s.resetPassword && s.password) {
    push(`ALTER USER ${userQ} IDENTIFIED BY ${qLiteral(s.password)};`);
  }

  if (s.validUntil) push(`ALTER USER ${userQ} PASSWORD EXPIRE INTERVAL 90 DAY;`);
  if (s.connectionLimit !== -1)
    push(`ALTER USER ${userQ} WITH MAX_USER_CONNECTIONS ${s.connectionLimit};`);
  push();

  if (s.mode === "update" && s.revokeBeforeGrant) {
    c("Revoke existing privileges on this database before re-grant.");
    push(`REVOKE ALL PRIVILEGES ON ${schemaTarget} FROM ${userQ};`);
    push();
  }

  const privs: string[] = [];
  if (s.privSelect) privs.push("SELECT");
  if (s.privInsert) privs.push("INSERT");
  if (s.privUpdate) privs.push("UPDATE");
  if (s.privDelete) privs.push("DELETE");
  if (s.privTruncate) privs.push("DROP"); // MySQL TRUNCATE requires DROP
  if (s.privReferences) privs.push("REFERENCES");
  if (s.privTrigger) privs.push("TRIGGER");
  if (s.privExecuteFn) privs.push("EXECUTE");
  if (s.privCreateSchema) privs.push("CREATE", "ALTER", "INDEX");

  if (privs.length) push(`GRANT ${privs.join(", ")} ON ${schemaTarget} TO ${userQ};`);
  c(
    "MySQL has no per-cluster CONNECT; user only sees DBs they have privileges on (default behavior).",
  );
  push(`FLUSH PRIVILEGES;`);
  return out.join("\n");
}

export function connectionStrings(s: BuilderState): ConnectionString[] {
  const pwd = encodeURIComponent(s.password);
  const user = encodeURIComponent(s.userName);
  const dbSeg = encodeURIComponent(s.dbName);
  const auth = pwd ? `${user}:${pwd}` : user;
  return [
    {
      label: "MySQL CLI / Connector",
      url: `mysql://${auth}@${s.host}:${s.port}/${dbSeg}`,
    },
    {
      label: "JDBC",
      url: `jdbc:mysql://${s.host}:${s.port}/${dbSeg}?user=${user}&password=${pwd}&useSSL=true`,
    },
    {
      label: "SQLAlchemy / Django",
      url: `mysql+pymysql://${auth}@${s.host}:${s.port}/${dbSeg}`,
    },
    { label: "Node.js (mysql2)", url: `mysql://${auth}@${s.host}:${s.port}/${dbSeg}` },
  ];
}
